/**
 * Admin Sidebar Manager
 * Provides dynamic sidebar configuration with admin-specific navigation items
 */

import React from 'react';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  SettingOutlined,
  UserOutlined,
  SecurityScanOutlined,
  TeamOutlined,
  DatabaseOutlined,
  MonitorOutlined,
  BugOutlined,
  ExperimentOutlined,
  BarChartOutlined,
  AlertOutlined,
  FileTextOutlined,
  LineChartOutlined,
  WalletOutlined,
  PlayCircleOutlined,
  ShoppingCartOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  BellOutlined,
  AppstoreOutlined,
  MessageOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { User } from '@/infrastructure/api';
import {
  getClientSidebarConfig,
  isClientAudience,
} from './clientSidebarConfig';

// Re-export for consumers that import from adminSidebarManager
export { isClientAudience, getClientSidebarConfig } from './clientSidebarConfig';

const icons = {
  DashboardOutlined,
  BarChartOutlined,
  SettingOutlined,
  UserOutlined,
  AlertOutlined,
  FileTextOutlined,
  LineChartOutlined,
  WalletOutlined,
  PlayCircleOutlined,
  ShoppingCartOutlined,
  ExperimentOutlined,
  LoadingOutlined,
  SecurityScanOutlined,
  TeamOutlined,
  DatabaseOutlined,
  MonitorOutlined,
  BugOutlined,
  QuestionCircleOutlined,
  BellOutlined,
  AppstoreOutlined,
  MessageOutlined,
  ThunderboltOutlined,
};

export interface AdminSidebarItem {
  key: string;
  label: string;
  icon: string;
  route: string;
  order: number;
  children?: AdminSidebarItem[];
  /** Required roles to show this item */
  requiredRoles?: string[];
  /** Required permissions to show this item */
  requiredPermissions?: string[];
  /** Whether admin access is required */
  requireAdmin?: boolean;
  /** Whether super admin access is required */
  requireSuperAdmin?: boolean;
}

export interface AdminSidebarConfig {
  items: AdminSidebarItem[];
  footer: AdminSidebarItem[];
}

// Base sidebar configuration (available to all users)
const baseSidebarItems: AdminSidebarItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: "DashboardOutlined",
    route: "/dashboard",
    order: 1
  },
  {
    key: "trading",
    label: "Trading",
    icon: "LineChartOutlined",
    route: "/trading",
    order: 2
  },
  {
    key: "portfolio",
    label: "Portfolio",
    icon: "WalletOutlined",
    route: "/portfolio",
    order: 3
  },
  {
    key: "agents",
    label: "Agents",
    icon: "ThunderboltOutlined",
    route: "/ai-trading",
    order: 4
  },
  {
    // CU 86bb8jrym — desk chat must be discoverable in primary nav
    key: "chat-ai",
    label: "Chat",
    icon: "MessageOutlined",
    route: "/chat-ai",
    order: 5
  },
  {
    key: "analytics",
    label: "Analytics",
    icon: "BarChartOutlined",
    route: "/analytics",
    order: 6
  },
  {
    key: "monitoring",
    label: "Monitoring",
    icon: "AlertOutlined",
    route: "/monitoring",
    order: 7,
    children: [
      {
        key: "alerts",
        label: "Alerts",
        icon: "AlertOutlined",
        route: "/alerts",
        order: 1
      },
      {
        key: "reports",
        label: "Reports",
        icon: "FileTextOutlined",
        route: "/reports",
        order: 2
      }
    ]
  },
  {
    key: "user",
    label: "User",
    icon: "UserOutlined",
    route: "/profile",
    order: 10,
    children: [
      {
        key: "profile",
        label: "Profile",
        icon: "UserOutlined",
        route: "/profile",
        order: 1
      },
      {
        key: "notifications",
        label: "Notifications",
        icon: "BellOutlined",
        route: "/notifications",
        order: 2
      },
      {
        key: "settings",
        label: "Settings",
        icon: "SettingOutlined",
        route: "/settings",
        order: 3
      }
    ]
  },
  {
    key: "help",
    label: "Help",
    icon: "QuestionCircleOutlined",
    route: "/help",
    order: 11
  }
];

// Admin-specific sidebar items
const adminSidebarItems: AdminSidebarItem[] = [
  {
    key: "admin",
    label: "Administration",
    icon: "SecurityScanOutlined",
    route: "/admin",
    order: 7,
    requireAdmin: true,
    children: [
      {
        key: "admin-dashboard",
        label: "Admin Dashboard",
        icon: "DashboardOutlined",
        route: "/admin",
        order: 1,
        requireAdmin: true
      },
      {
        key: "user-management",
        label: "User Management",
        icon: "TeamOutlined",
        route: "/admin/users",
        order: 2,
        requireAdmin: true
      },
      {
        key: "system-monitoring",
        label: "System Monitoring",
        icon: "MonitorOutlined",
        route: "/admin/monitoring",
        order: 3,
        requireAdmin: true
      },
      {
        key: "audit-logs",
        label: "Audit Logs",
        icon: "FileTextOutlined",
        route: "/admin/audit",
        order: 4,
        requireAdmin: true
      },
      {
        key: "data-quality",
        label: "Data Quality",
        icon: "DatabaseOutlined",
        route: "/data-quality",
        order: 5,
        requireAdmin: true
      },
      {
        key: "broker-integration",
        label: "Broker Integration",
        icon: "SettingOutlined",
        route: "/broker-integration",
        order: 6,
        requireAdmin: true
      },
      {
        key: "risk-management",
        label: "Risk Management",
        icon: "AlertOutlined",
        route: "/risk-management",
        order: 7,
        requireAdmin: true
      }
    ]
  },
  {
    key: "audit",
    label: "Audit Portal",
    icon: "SecurityScanOutlined",
    route: "/audit",
    order: 8,
    requireAdmin: true
  },
  {
    key: "auditors",
    label: "Auditors",
    icon: "SecurityScanOutlined",
    route: "/auditors",
    order: 9,
    requiredRoles: ["auditor", "compliance_officer"],
    children: [
      {
        key: "audit-dashboard",
        label: "Audit Dashboard",
        icon: "DashboardOutlined",
        route: "/auditors/dashboard",
        order: 1,
        requiredRoles: ["auditor", "compliance_officer"]
      },
      {
        key: "compliance-reports",
        label: "Compliance Reports",
        icon: "FileTextOutlined",
        route: "/auditors/compliance",
        order: 2,
        requiredRoles: ["auditor", "compliance_officer"]
      },
      {
        key: "risk-assessments",
        label: "Risk Assessments",
        icon: "AlertOutlined",
        route: "/auditors/risk",
        order: 3,
        requiredRoles: ["auditor", "compliance_officer"]
      },
      {
        key: "audit-trails",
        label: "Audit Trails",
        icon: "FileTextOutlined",
        route: "/auditors/trails",
        order: 4,
        requiredRoles: ["auditor", "compliance_officer"]
      },
      {
        key: "investigations",
        label: "Investigations",
        icon: "SecurityScanOutlined",
        route: "/auditors/investigations",
        order: 5,
        requiredRoles: ["auditor", "compliance_officer"]
      }
    ]
  }
];

// Super admin-specific items
const superAdminSidebarItems: AdminSidebarItem[] = [
  {
    key: "super-admin",
    label: "Super Admin",
    icon: "BugOutlined",
    route: "/admin/super",
    order: 12,
    requireSuperAdmin: true,
    children: [
      {
        key: "system-config",
        label: "System Configuration",
        icon: "SettingOutlined",
        route: "/admin/system-config",
        order: 1,
        requireSuperAdmin: true
      },
      {
        key: "security-settings",
        label: "Security Settings",
        icon: "SecurityScanOutlined",
        route: "/admin/security",
        order: 2,
        requireSuperAdmin: true
      }
    ]
  }
];

// Development items (only for development environment)
const developmentSidebarItems: AdminSidebarItem[] = [
  {
    key: "development",
    label: "Development",
    icon: "ExperimentOutlined",
    route: "/debug",
    order: 13,
    children: [
      {
        key: "debug",
        label: "Debug",
        icon: "ExperimentOutlined",
        route: "/debug",
        order: 1
      },
      {
        key: "test-connection",
        label: "Test Connection",
        icon: "ExperimentOutlined",
        route: "/test-connection",
        order: 2
      },
      {
        key: "theme-demo",
        label: "Theme Demo",
        icon: "ExperimentOutlined",
        route: "/theme-demo",
        order: 3
      },
      {
        key: "demo",
        label: "Demo",
        icon: "PlayCircleOutlined",
        route: "/demo",
        order: 4
      },
      {
        key: "theme-test",
        label: "Theme Test",
        icon: "ExperimentOutlined",
        route: "/theme-test",
        order: 5
      },
      {
        key: "color-test",
        label: "Color Test",
        icon: "ExperimentOutlined",
        route: "/color-test",
        order: 6
      },
      {
        key: "loading-demo",
        label: "Loading Demo",
        icon: "LoadingOutlined",
        route: "/loading-demo",
        order: 7
      }
    ]
  }
];

/**
 * Check if user has required access for a sidebar item
 */
function hasItemAccess(item: AdminSidebarItem, user: User | null): boolean {
  // Base items (without special requirements) should be available to all users, including null
  // Only items with special requirements need a user
  const hasSpecialRequirements = item.requireSuperAdmin || item.requireAdmin ||
    (item.requiredRoles && item.requiredRoles.length > 0) ||
    (item.requiredPermissions && item.requiredPermissions && item.requiredPermissions.length > 0);

  // If item has no special requirements, allow access even without user
  if (!hasSpecialRequirements) return true;

  // For items with special requirements, user must exist
  if (!user) return false;

  // Helper function to check if user has role (checking clearance level and permissions)
  const hasUserRole = (role: string): boolean => {
    const clearanceLevel = user.clearance_level || 'unclassified';
    const permissions = (user as any).permissions || [];
    return clearanceLevel === 'top_secret' || clearanceLevel === role || permissions.includes(role);
  };

  // Check super admin requirement
  if (item.requireSuperAdmin) {
    return hasUserRole('super_admin');
  }

  // Check admin requirement
  if (item.requireAdmin) {
    return hasUserRole('admin') || hasUserRole('super_admin');
  }

  // Check required roles
  if (item.requiredRoles && item.requiredRoles.length > 0) {
    const hasRequiredRole = item.requiredRoles.some(role => hasUserRole(role));
    if (!hasRequiredRole) return false;
  }

  // Check required permissions (not available in current API)
  if (item.requiredPermissions && item.requiredPermissions.length > 0) {
    // TODO: Implement when backend provides permissions
    // For now, return true to avoid blocking access
    return true;
  }

  return true;
}

/**
 * Filter sidebar items based on user permissions
 */
function filterSidebarItems(items: AdminSidebarItem[], user: User | null): AdminSidebarItem[] {
  return items
    .filter(item => hasItemAccess(item, user))
    .map(item => ({
      ...item,
      children: item.children ? filterSidebarItems(item.children, user) : undefined
    }))
    .filter(item => {
      // Remove items with no accessible children
      if (item.children && item.children.length === 0) {
        return false;
      }
      return true;
    });
}

/**
 * Get dynamic sidebar configuration based on user roles / audience.
 *
 * Clients receive a curated menu only (no Monitoring/Development/admin).
 * Employees/admins keep the permission-based dynamic menu.
 */
export function getDynamicSidebarConfig(user: User | null): AdminSidebarConfig {
  // Client audience: never expose employee/admin/dev navigation clusters
  if (isClientAudience(user)) {
    return getClientSidebarConfig();
  }

  let allItems = [...baseSidebarItems];

  // Helper function to check if user has role (checking roles array, role field, and permissions)
  const hasUserRole = (role: string): boolean => {
    if (!user) return false;
    const clearanceLevel = user.clearance_level || 'unclassified';
    const permissions = (user as any).permissions || [];
    return clearanceLevel === 'top_secret' || clearanceLevel === role || permissions.includes(role);
  };

  // Add admin items if user has admin access
  if (user && (hasUserRole('admin') || hasUserRole('super_admin'))) {
    allItems = [...allItems, ...adminSidebarItems];
  }

  // Add super admin items if user has super admin access
  if (user && hasUserRole('super_admin')) {
    allItems = [...allItems, ...superAdminSidebarItems];
  }

  // Development items: employees only (clients already returned above)
  if (process.env.NODE_ENV === 'development') {
    allItems = [...allItems, ...developmentSidebarItems];
  }

  // Filter items based on user permissions
  const filteredItems = filterSidebarItems(allItems, user);

  return {
    items: filteredItems,
    footer: []
  };
}

/**
 * Get icon component by name
 */
export function getIcon(name: string): React.ComponentType {
  const Icon = icons[name as keyof typeof icons];
  return Icon || DashboardOutlined;
}

/**
 * Convert sidebar items to Ant Design menu items.
 * Icons are Ant Design SVG components only (no emoji). Routes unchanged.
 */
export function toMenuItems(items: AdminSidebarItem[]): MenuProps['items'] {
  return items
    .sort((a, b) => a.order - b.order)
    .map(item => {
      const Icon = getIcon(item.icon) as React.ComponentType<{ className?: string }>;
      return {
        key: item.key,
        label: React.createElement('span', { className: 'sidebar-menu-text' }, item.label),
        // title enables antd tooltip when rail is collapsed (hierarchy preserved)
        title: item.label,
        icon: React.createElement(Icon),
        className: item.children?.length
          ? 'sidebar-menu-item sidebar-menu-parent'
          : 'sidebar-menu-item',
        children: item.children ? toMenuItems(item.children) : undefined,
      };
    });
}

/**
 * Get routes mapping from sidebar items
 */
export function getRoutes(items: AdminSidebarItem[]): Record<string, string> {
  const routes: Record<string, string> = {};

  function process(arr: AdminSidebarItem[]) {
    arr.forEach(item => {
      routes[item.key] = item.route;
      if (item.children) process(item.children);
    });
  }

  process(items);
  return routes;
}

/**
 * Get path mapping from sidebar items
 */
export function getPathMap(items: AdminSidebarItem[]): Record<string, string> {
  const paths: Record<string, string> = {};

  function process(arr: AdminSidebarItem[]) {
    arr.forEach(item => {
      paths[item.route] = item.key;
      if (item.children) process(item.children);
    });
  }

  process(items);
  return paths;
}
